import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { shopApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ShoppingCart, MapPin, Truck, ArrowLeft } from 'lucide-react';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { getPendingPurchase, clearPendingPurchase } from '../components/PaymentMethodModal';

const API_URL = window.location.origin;

const CheckoutPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [shipping, setShipping] = useState({
    full_name: user?.name || '',
    country: '',
    city: '',
    postal_code: '',
    address: '',
    address_line2: '',
    phone: '',
  });

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    shopApi.getCart().then(d => { setCart(d); setLoading(false); }).catch(() => setLoading(false));
  }, [isAuthenticated, navigate]);

  // Auto-open payment modal if redirected back from add-card flow
  useEffect(() => {
    const pending = getPendingPurchase();
    if (pending?.autoOpen && pending.paymentType === 'merchandise') {
      clearPendingPurchase();
      setTimeout(() => setShowPaymentModal(true), 500);
    }
  }, []);

  const handleChange = (field, value) => setShipping(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const required = ['full_name', 'country', 'city', 'postal_code', 'address', 'phone'];
    for (const f of required) {
      if (!shipping[f]?.trim()) {
        toast.error(`Please fill in ${f.replace(/_/g, ' ')}`);
        return;
      }
    }
    if (cart.items.length === 0) { toast.error('Cart is empty'); return; }
    // Open payment method selection
    setShowPaymentModal(true);
  };

  const handleShopCheckout = async (paymentMethod) => {
    const result = await shopApi.checkout(shipping, API_URL, paymentMethod);
    return {
      status: 'completed',
      amount: result.total,
      payment_method: result.payment_method || paymentMethod,
      new_balance: result.new_balance,
      balance_used: result.balance_used,
      card_charged: result.card_charged,
      credit_back: result.credit_back,
    };
  };

  if (loading) return <div className="min-h-screen bg-[#030014]"><Navbar /><div className="flex items-center justify-center h-96"><div className="spinner" /></div></div>;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="checkout-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-28 pb-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-2xl font-bold mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Shipping Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-5" data-testid="shipping-form">
            <div className="admin-card p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#8B5CF6]" /> Shipping Address</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Full Name *</label>
                  <Input value={shipping.full_name} onChange={e => handleChange('full_name', e.target.value)} placeholder="John Doe" data-testid="shipping-name" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Country *</label>
                    <Input value={shipping.country} onChange={e => handleChange('country', e.target.value)} placeholder="Italy" data-testid="shipping-country" required />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">City *</label>
                    <Input value={shipping.city} onChange={e => handleChange('city', e.target.value)} placeholder="Rome" data-testid="shipping-city" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Postal Code *</label>
                    <Input value={shipping.postal_code} onChange={e => handleChange('postal_code', e.target.value)} placeholder="00100" data-testid="shipping-postal" required />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Phone Number *</label>
                    <Input value={shipping.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+39 123 456 7890" data-testid="shipping-phone" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Address *</label>
                  <Input value={shipping.address} onChange={e => handleChange('address', e.target.value)} placeholder="Via Roma 1" data-testid="shipping-address" required />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Address Line 2 (optional)</label>
                  <Input value={shipping.address_line2} onChange={e => handleChange('address_line2', e.target.value)} placeholder="Apt, Suite, Floor" data-testid="shipping-address2" />
                </div>
              </div>
            </div>
            <Button type="submit" disabled={cart.items.length === 0} className="w-full py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-medium" data-testid="place-order-btn">
              <Truck className="w-4 h-4 mr-2" />
              {`Place Order — $${cart.total.toFixed(2)}`}
            </Button>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="admin-card p-6 sticky top-28">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#8B5CF6]" /> Order Summary</h2>
              {cart.items.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Cart is empty</p>
              ) : (
                <div className="space-y-3">
                  {cart.items.map((item, idx) => (
                    <div key={idx} className="flex gap-3 py-2 border-b border-white/5 last:border-0">
                      {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.product_name}</p>
                        {Object.keys(item.selected_variants || {}).length > 0 && (
                          <p className="text-xs text-[#8B5CF6]">{Object.entries(item.selected_variants).map(([k,v]) => `${k}: ${v}`).join(' / ')}</p>
                        )}
                        <p className="text-xs text-gray-500">Qty: {item.quantity} × ${item.price.toFixed(2)}</p>
                      </div>
                      <p className="text-sm font-semibold">${item.line_total.toFixed(2)}</p>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-lg">${cart.total.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal for Shop */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentType="merchandise"
        amount={cart.total}
        shopCheckoutFn={handleShopCheckout}
        onSuccess={() => {
          window.dispatchEvent(new Event('cart-updated'));
          navigate('/my-orders');
        }}
      />
    </div>
  );
};

export default CheckoutPage;
